import React, { useState, useEffect } from 'react';
import { Card, Table, Modal, Input, Space, message, Tag, Tooltip } from 'antd';
import { PlusOutlined, CopyOutlined, DeleteOutlined, EyeOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import loggingApi from '../../interceptors/loggingApi';
import StandardButton from '../StandardButton';

const ApplicationsList = ({ onSelectApplication }) => {
  const { t } = useTranslation();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [newAppName, setNewAppName] = useState('');
  const [newAppDesc, setNewAppDesc] = useState('');
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    fetchApplications();
  }, []);

  const fetchApplications = async () => {
    setLoading(true);
    try {
      const response = await loggingApi.get('/applications');
      setApplications(response.data);
    } catch (error) {
      message.error(t('failed_to_load_applications'));
    } finally {
      setLoading(false);
    }
  };

  const createApplication = async () => {
    if (!newAppName.trim()) {
      message.error(t('enter_application_name'));
      return;
    }

    setCreating(true);
    try {
      const response = await loggingApi.post('/applications', {
        name: newAppName.trim(),
        description: newAppDesc.trim() || null
      });
      
      setApplications([response.data, ...applications]);
      setModalVisible(false);
      setNewAppName('');
      setNewAppDesc('');
      message.success(t('application_created'));
    } catch (error) {
      message.error(t('failed_to_create_application'));
    } finally {
      setCreating(false);
    }
  };

  const copyApiKey = (apiKey) => {
    navigator.clipboard.writeText(apiKey);
    message.success(t('api_key_copied'));
  };

  const deleteApplication = async (id) => {
    Modal.confirm({
      title: t('delete_application'),
      content: t('delete_application_confirm'),
      onOk: async () => {
        try {
          await loggingApi.delete(`/applications/${id}`);
          setApplications(applications.filter(app => app.id !== id));
          message.success(t('application_deleted'));
        } catch (error) {
          message.error(t('failed_to_delete_application'));
        }
      }
    });
  };

  const columns = [
    {
      title: t('name'),
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: t('description'),
      dataIndex: 'description',
      key: 'description',
      render: (text) => text || '-',
    },
    {
      title: t('api_key'),
      dataIndex: 'api_key',
      key: 'api_key',
      render: (text) => (
        <Space>
          <code style={{ fontSize: 12 }}>{text.substring(0, 8)}...{text.substring(text.length - 8)}</code>
          <Tooltip title={t('copy_api_key')}>
            <StandardButton
              type="secondary"
              icon={<CopyOutlined />}
              size="small"
              aria-label={t('copy_api_key')}
              onClick={() => copyApiKey(text)}
            />
          </Tooltip>
        </Space>
      ),
    },
    {
      title: t('log_counts'),
      key: 'log_counts',
      render: (_, record) => (
        <Space>
          {record.log_counts?.error > 0 && (
            <Tag color="error">{record.log_counts.error} {t('errors')}</Tag>
          )}
          {record.log_counts?.warning > 0 && (
            <Tag color="warning">{record.log_counts.warning} {t('warnings')}</Tag>
          )}
          {record.log_counts?.info > 0 && (
            <Tag color="processing">{record.log_counts.info} {t('info')}</Tag>
          )}
        </Space>
      ),
    },
    {
      title: t('created_at'),
      dataIndex: 'created_at',
      key: 'created_at',
      render: (text) => new Date(text).toLocaleString(),
    },
    {
      title: t('actions'),
      key: 'actions',
      render: (_, record) => (
        <Space>
          <StandardButton
            type="secondary"
            icon={<EyeOutlined />}
            onClick={() => onSelectApplication(record)}
          >
            {t('view_logs')}
          </StandardButton>
          <StandardButton
            type="secondary"
            danger
            icon={<DeleteOutlined />}
            onClick={() => deleteApplication(record.id)}
          >
            {t('delete')}
          </StandardButton>
        </Space>
      ),
    },
  ];

  return (
    <>
      <Card
        title={t('applications')}
        extra={
          <StandardButton
            icon={<PlusOutlined />}
            onClick={() => setModalVisible(true)}
          >
            {t('new_application')}
          </StandardButton>
        }
      >
        <Table
          columns={columns}
          dataSource={applications}
          loading={loading}
          rowKey="id"
          pagination={{ pageSize: 10 }}
        />
      </Card>

      <Modal
        title={t('create_new_application')}
        open={modalVisible}
        onOk={createApplication}
        onCancel={() => {
          setModalVisible(false);
          setNewAppName('');
          setNewAppDesc('');
        }}
        confirmLoading={creating}
        okText={t('create')}
        cancelText={t('cancel')}
      >
        <Input
          placeholder={t('application_name')}
          value={newAppName}
          onChange={(e) => setNewAppName(e.target.value)}
          style={{ marginBottom: 16 }}
          onPressEnter={createApplication}
        />
        <Input.TextArea
          placeholder={t('description_optional')}
          value={newAppDesc}
          onChange={(e) => setNewAppDesc(e.target.value)}
          rows={3}
        />
      </Modal>
    </>
  );
};

export default ApplicationsList;